import { LoginProvidersInnerType } from "@eai-toolkit/client";
import {
  Button,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import CenteredLayout from "../../components/Centered";
import { ServiceNowLogo, SsoKey } from "../../icons";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchProviders, oidcLogin } from "../../store/slices/authSlice";

const PaperStyled = styled(Paper)({
  padding: 20,
  minHeight: "70vh",
  width: 400,
  margin: "20px auto",
});

const TextCe = styled("h2")({
  textAlign: "center",
});

const InputStyle = styled(TextField)({
  margin: "8px",
  marginLeft: "0px",
});

const ErrorStyle = styled("h3")({
  color: "red",
  textAlign: "center",
});

const OrSignInWith = styled("p")({
  position: "relative",
  textAlign: "center",
  marginTop: "12px",
  marginBottom: "24px",
});

const SignInButton = styled(Button)({
  display: "flex",
  margin: "8px",
  marginLeft: "0px",
  marginBottom: "16px",
});

const OidcButton = styled(Grid)({
  margin: "8px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const IconButtonStyled = styled(IconButton)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

function UiLogin() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isLoading, authError, redirect, providers } = useAppSelector(
    (state) => state.auth,
  );
  const formRef = useRef<HTMLFormElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { login_challenge, error } = (() => {
    const params = new URLSearchParams(window.location.search);
    return {
      login_challenge: params.get("login_challenge"),
      error: params.get("error"),
    };
  })();

  useEffect(() => {
    if (user) {
      if (redirect && redirect !== "") {
        window.location.href = redirect;
      } else {
        navigate("/");
      }
    }
  }, [user, navigate, redirect]);

  useEffect(() => {
    // Function to fetch available identity providers
    dispatch(fetchProviders());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      setLoginError(error as string);
    }
  }, [error]);

  // Instead of storing state for all form changes, and since we're not doing anything with the inputs, the form fields are uncontrolled.
  // This useEffect will watch for changes and set a state flag if both fields have been filled out using the FormData API.
  useEffect(() => {
    const form = formRef.current;
    const button = buttonRef.current;

    if (form && button) {
      const checkFormValidity = () => {
        const formData = new FormData(form);
        const username = formData.get("username") as string;
        const password = formData.get("password") as string;
        setIsFormValid(Boolean(username?.trim() && password?.trim()));
      };

      form.addEventListener("input", checkFormValidity);
      checkFormValidity(); // Initial check

      return () => form.removeEventListener("input", checkFormValidity);
    }
  }, [isLoading, isSubmitting]);

  // Return error if user reached login page without hydra challenge
  if (!login_challenge) {
    return (
      <CenteredLayout>
        An error occured...click <a href="/">here</a>
      </CenteredLayout>
    );
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.currentTarget as HTMLFormElement;
    form.submit();
  };

  const handleOidcLogin = async (providerId: string) => {
    try {
      dispatch(
        oidcLogin({
          providerId,
          loginChallenge: login_challenge as string,
        }),
      );
    } catch (error) {
      console.error("OIDC login error:", error);
    }
  };

  const ldapProviders =
    providers?.filter(
      (provider) => provider.type === LoginProvidersInnerType.ldap,
    ) || [];
  const oidcProviders =
    providers?.filter(
      (provider) => provider.type === LoginProvidersInnerType.oidc,
    ) || [];
  const isSubmitButtonDisabled = !isFormValid || isLoading || isSubmitting;

  return (
    <CenteredLayout>
      <Grid>
        <PaperStyled elevation={10}>
          <Grid>
            <TextCe>
              <ServiceNowLogo />
            </TextCe>
            <TextCe>Sign In</TextCe>
            {ldapProviders.length > 0 && (
              <form
                method="POST"
                action="/login/ldap"
                onSubmit={handleSubmit}
                ref={formRef}
              >
                <input
                  type="hidden"
                  name="challenge"
                  value={login_challenge as string}
                />
                <ErrorStyle>{authError}</ErrorStyle>
                {loginError && <ErrorStyle>{loginError}</ErrorStyle>}
                <InputStyle
                  label="Username"
                  placeholder="Enter username"
                  variant="outlined"
                  name="username"
                  fullWidth
                  required
                />
                <InputStyle
                  label="Password"
                  placeholder="Enter password"
                  type="password"
                  name="password"
                  variant="outlined"
                  fullWidth
                  required
                />
                <SignInButton
                  ref={buttonRef}
                  type="submit"
                  size="large"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitButtonDisabled}
                  fullWidth
                >
                  {isLoading ? "Please wait..." : "Sign In"}
                </SignInButton>
              </form>
            )}
            {oidcProviders.length > 0 && (
              <>
                {ldapProviders.length > 0 && (
                  <div>
                    <OrSignInWith>
                      <span>Or sign in with</span>
                    </OrSignInWith>
                  </div>
                )}
                <Grid container spacing={2} justifyContent="center">
                  {oidcProviders.map((provider) => (
                    <OidcButton item key={provider.id}>
                      <IconButtonStyled
                        color="primary"
                        onClick={() => handleOidcLogin(provider.id)}
                      >
                        <SsoKey />
                        <Typography variant="caption">
                          {provider.name}
                        </Typography>
                      </IconButtonStyled>
                    </OidcButton>
                  ))}
                </Grid>
              </>
            )}
          </Grid>
        </PaperStyled>
      </Grid>
    </CenteredLayout>
  );
}

export default UiLogin;
